import * as React from 'react';
import { CheckLine, CopyLine } from '@mingcute/react';

import { cn } from '../lib/cn';

export interface CodeBlockProps extends React.HTMLAttributes<HTMLDivElement> {
  code: string;
  language?: string;
  copyable?: boolean;
  /** When true, wraps long lines instead of horizontal scrolling. */
  wrap?: boolean;
}

export const CodeBlock = React.forwardRef<HTMLDivElement, CodeBlockProps>(
  ({ code, language, copyable = true, wrap, className, ...props }, ref) => {
    const [copied, setCopied] = React.useState(false);

    const onCopy = React.useCallback(async () => {
      try {
        await navigator.clipboard.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      } catch {
        /* noop */
      }
    }, [code]);

    return (
      <div
        ref={ref}
        className={cn(
          'group relative overflow-hidden rounded-lg border bg-[var(--color-neutral-900)] border-[var(--color-border)]',
          className,
        )}
        {...props}
      >
        {(language || copyable) && (
          <div className="flex items-center justify-between border-b border-[var(--color-border)] bg-[var(--color-neutral-800)] px-3 py-1.5">
            <span className="text-[11px] font-mono uppercase tracking-wider text-[var(--color-fg-muted)]">
              {language ?? ''}
            </span>
            {copyable && (
              <button
                type="button"
                onClick={onCopy}
                aria-label={copied ? 'Copied' : 'Copy code'}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded px-2 py-1 text-xs font-medium',
                  'text-[var(--color-fg-secondary)] hover:text-[var(--color-fg)] hover:bg-[var(--color-subtle)]',
                  'focus-visible:outline-none',
                  'transition-colors',
                )}
              >
                {copied ? <CheckLine size={14} /> : <CopyLine size={14} />}
                {copied ? 'Copied' : 'Copy'}
              </button>
            )}
          </div>
        )}
        <pre
          className={cn(
            'm-0 px-4 py-3 text-[13px] leading-relaxed text-[var(--color-fg)] font-mono',
            wrap ? 'whitespace-pre-wrap break-words' : 'overflow-x-auto',
          )}
        >
          <code>{code}</code>
        </pre>
      </div>
    );
  },
);
CodeBlock.displayName = 'CodeBlock';
